/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { Configuration, LogLevel } from "@azure/msal-browser";

declare const __ADDIN_ORIGIN__: string | undefined;
declare const __FRONTEND_CLIENT_ID__: string | undefined;
declare const __BACKEND_API_SCOPE__: string | undefined;
declare const __LOCAL_DEV_MODE__: string | undefined;

const normalizeString = (raw?: string): string | undefined => {
  if (typeof raw !== "string") {
    return undefined;
  }
  const trimmed = raw.trim();
  const unquoted = trimmed.replace(/^['"]+/, "").replace(/['"]+$/, "");
  return unquoted || undefined;
};

/**
 * Normalize an "origin-like" config value (scheme + host + optional port).
 *
 * Why: it's common to accidentally set `ADDIN_ORIGIN` with a trailing slash
 * (for example `https://addin.example.com/`). If we then build redirect URIs via
 * `${origin}/taskpane.html`, we end up with `//taskpane.html` which *does not*
 * match Entra redirect URIs (exact-string match).
 *
 * We intentionally return only the URL origin (no path/query/hash).
 */
const normalizeOrigin = (raw?: string): string | undefined => {
  const normalized = normalizeString(raw);
  if (!normalized) {
    return undefined;
  }

  // Prefer URL parsing so we reliably strip any accidental path and any trailing slash.
  try {
    return new URL(normalized).origin;
  } catch (_error) {
    // Fallback: be tolerant in case the value isn't a fully-qualified URL.
    return normalized.replace(/\/+$/, "");
  }
};

const isLocalhostOrigin = (origin: string | undefined): boolean => {
  if (!origin) {
    return false;
  }
  return (
    origin.startsWith("https://localhost") ||
    origin.startsWith("https://127.0.0.1") ||
    origin.startsWith("http://127.0.0.1")
  );
};

const runtimeOrigin =
  typeof window !== "undefined" && window.location && typeof window.location.origin === "string"
    ? window.location.origin
    : undefined;

const addinOrigin = normalizeOrigin(__ADDIN_ORIGIN__) ?? runtimeOrigin;
const msalClientId = normalizeString(__FRONTEND_CLIENT_ID__);
const backendApiScope = normalizeString(__BACKEND_API_SCOPE__);
const localDevMode = normalizeString(__LOCAL_DEV_MODE__)?.toLowerCase() === "true";

// This scope is required in *all* environments because it determines which backend API
// the frontend requests an access token for (and therefore what the consent dialog references).
if (!localDevMode) {
  if (!backendApiScope) {
    throw new Error(
      "BACKEND_API_SCOPE must be set (expected format: api://<BACKEND_CLIENT_ID>/access_as_user)."
    );
  }
}

const buildRedirectUri = (origin: string | undefined, path: string): string => {
  const base = origin || "https://localhost:3000";
  // Ensure we don't accidentally generate `//<path>`.
  const normalizedBase = base.replace(/\/+$/, "");
  const normalizedPath = path.replace(/^\/+/, "");
  return `${normalizedBase}/${normalizedPath}`;
};

// Fail closed for hosted environments (avoid accidental localhost or missing config in production).
if (!localDevMode && !isLocalhostOrigin(addinOrigin)) {
  if (!addinOrigin) {
    throw new Error("ADDIN_ORIGIN must be set for non-local builds.");
  }
  if (!addinOrigin.startsWith("https://")) {
    throw new Error("ADDIN_ORIGIN must be HTTPS for non-local builds.");
  }
  if (!msalClientId) {
    throw new Error("FRONTEND_CLIENT_ID must be set for non-local builds.");
  }
}

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig: Configuration = {
  auth: {
    // This is the V2 endpoint for Microsoft identity platform.
    authority: "https://login.microsoftonline.com/organizations",
    // This is the ONLY mandatory field that you need to supply.
    // It is the Application (client) ID from the app registration in the Azure portal.
    clientId: msalClientId || "",
    // The redirect URI for single-page applications (SPA).
    // MSAL will redirect the user back to this URI after they successfully log in.
    // It's best practice to use an absolute URL.
    //
    // Important: for Office add-ins we use a minimal `auth.html` landing page so popup flows
    // don't depend on `Office.onReady()` (which won't fire in the popup).
    redirectUri: buildRedirectUri(addinOrigin, "taskpane.html"),
    // After logout, return to the task pane entry so the add-in re-hydrates cleanly
    postLogoutRedirectUri: buildRedirectUri(addinOrigin, "taskpane.html"),
  },
  cache: {
    // This configures where your cache will be stored.
    //
    // Security note:
    // - `sessionStorage`/`localStorage` are readable by any script running in the page.
    //   If an XSS bug ever lands in the task pane, tokens stored there are trivial to steal.
    // - `memoryStorage` keeps tokens in JS memory only (cleared when the task pane reloads).
    //
    // Tradeoff:
    // - Users may need to re-authenticate if the task pane is closed/reloaded.
    cacheLocation: "memoryStorage",
    // Set this to "true" if you are having issues on IE11 or Edge
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
        }
      },
    },
    allowRedirectInIframe: true,
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) for Microsoft identity platform V2.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: [
    /**
     * Custom delegated scope exposed by the backend app registration ("Expose an API").
     *
     * Example: api://<BACKEND_CLIENT_ID>/access_as_user
     */
    backendApiScope,
  ],
  // Request authorization code flow with PKCE so we can exchange the code for a refresh token on the backend
  responseMode: "fragment" as const,
};

/**
 * Submit-job request scopes (Just-in-Time escalation).
 *
 * We request `offline_access` only when the user starts a batch job so the backend can
 * perform the On-Behalf-Of exchange and obtain a refresh token scoped to that batch.
 * This keeps the initial login (UI entitlement) least-privilege.
 */
export const submitJobRequest = {
  scopes: [
    ...loginRequest.scopes,
    // `offline_access` asks Entra to mint refresh tokens so the backend can keep working on behalf of the user.
    "offline_access",
  ],
  responseMode: loginRequest.responseMode,
};
